import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(theme => ({
  gradientBar: {
    width: (props) => props.width || (props.maxScale === 10 ? '500px' : '380px'),
    maxWidth: '100%',
    // Opt-in via the barHeight prop. Score4c passes none and keeps the
    // original 24px, so only the case-master scale grows.
    height: (props) => props.barHeight || '24px',
    background: (props) => props.maxScale === 10
      ? `linear-gradient(90deg, #C51407 0%, #E2483C 11.11%, #F5B847 22.22%, #F5B847 33.33%, #F5B847 44.44%, #F5B847 55.56%, #F5B847 66.67%, #F5B847 77.78%, #009970 88.89%, #00664A 100%)`
      : `linear-gradient(90deg, #C51407 0%, #E2483C 25.36%, #F5B847 50.33%, #009970 75.12%, #00664A 100%)`,
    border: `1px solid ${theme.palette.neutral['clr-400']}`,
    // Rounded ends ride along with the markers rather than changing the shape
    // of the bar Score4c renders.
    borderRadius: (props) => (props.rounded ? '6px' : 0),
    position: 'relative',
  },
  divider: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    backgroundColor: theme.palette.shades['clr-white-900'],
  },
  line: {
    width: 0,
    height: '40px',
    borderLeft: '3px solid',
    borderLeftColor: theme.palette.neutral['clr-900'],
    zIndex: 12,
    position: 'relative',
    bottom: '7px',
    left: '0.5px',
  },
  triangle: {
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: '10px solid',
    borderBottomColor: theme.palette.neutral['clr-900'],
    transform: 'rotate(180deg)',
  },
  scaleContainer: {
    width: '20px',
    height: 'fit-content',
    position: 'absolute',
    right: '-10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  '@keyframes smoothAnimation': {
    '0%': { width: '0%' },
    '100%': { width: (props) => `${props.scale}%` },
  },
  innerBar: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    animationName: '$smoothAnimation',
    animationDuration: '1s',
    animationTimingFunction: 'ease-in',
    animationFillMode: 'forwards',
    borderRadius: '4px 0px 0px 4px',
  },
  marker: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transform: 'translateX(-50%)',
    zIndex: 12,
  },
  // A label is a value line with one or more captions stacked under it. Both
  // blocks sit outside the column flow, so the head holds its position however
  // tall the label grows.
  markerLabelAbove: {
    position: 'absolute',
    bottom: '100%',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  // Clears the scale numerals, which the lengthened stem already runs past.
  markerLabelBelow: {
    position: 'absolute',
    top: '100%',
    marginTop: '2px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  // Custom typography variants render as inline spans, so each line of a label
  // has to be told to take a row of its own.
  labelValue: {
    display: 'block',
    color: theme.palette.neutral['clr-900'],
  },
  labelCaption: {
    display: 'block',
  },
  // Only the current score breaks its value onto a line of its own. The two
  // benchmarks read as one line each - value then caption, at one size and in
  // one colour, with the value left bold to carry the emphasis - which is what
  // keeps a stepped-up label clear of the one below it, since a single line is
  // shorter than the row step that separates them.
  labelInline: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  // Every head is centred in a slot of one fixed height, so a diamond, a ring
  // and the wider concentric pair all land on the same line instead of each
  // shape's own size deciding where its centre sits.
  // Raised above the stem so it hides the top of the line, which is run up to
  // the slot's centre rather than stopped at its edge. A 20px circle in a 28px
  // slot would otherwise leave 4px of daylight between the head and its line,
  // and a shape that reached the slot's edge would be the only one touching it.
  // Every head now has the line arriving at its centre whatever its size.
  headSlot: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
  },
  // One head per marker kind. Colours come from the caller, so these carry only
  // the shape: an outlined ring for the current score, the same ring with a
  // filled centre once that score has reached the goal, a filled dot for the
  // next achievable one, a diamond for the ideal-candidate goal, and concentric
  // circles where two of them land on the same value. The circles are 20px and
  // the diamond's square is sized so its diagonal matches them.
  markerRing: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    borderWidth: '3px',
    borderStyle: 'solid',
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxSizing: 'border-box',
  },
  markerRingCore: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  markerFilled: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
  },
  markerDiamond: {
    width: '14px',
    height: '14px',
    transform: 'rotate(45deg)',
  },
  markerConcentric: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '27px',
    height: '27px',
    borderRadius: '50%',
    borderWidth: '3px',
    borderStyle: 'solid',
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxSizing: 'border-box',
  },
  markerConcentricCore: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
  },
  markerStem: {
    width: 0,
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderLeftColor: theme.palette.neutral['clr-900'],
  },
  '@keyframes markerPulse': {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.35)' },
    '100%': { transform: 'scale(1)' },
  },
  // Lands on the destination marker once the sweep has finished crossing the
  // gap, then settles - the delay matches the sweep's duration.
  markerPulse: {
    animation: '$markerPulse 0.85s ease-in-out 1.5s 2 both',
  },
  '@keyframes gapGrow': {
    '0%': { transform: 'scaleX(0)' },
    '100%': { transform: 'scaleX(1)' },
  },
  gap: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 11,
  },
  // The gap between the two scores was previously a translucent shimmer sliding
  // across once. Over a gap as narrow as half a point - about 5% of the bar -
  // there was nothing to see, and a single pass was gone before anyone looked.
  // It now grows out of the current score and holds, so the ground still to
  // cover stays marked after the animation finishes.
  //
  // How far the alpha can drop is set by the amber that covers most of the
  // mid-scale: it carries so much red and green that a blue laid over it washes
  // out to grey long before it turns see-through. At 0.55 the mix came out
  // rgb(121,134,137), a flat grey. 0.82 is about as low as it goes while still
  // reading blue everywhere - roughly rgb(82,124,191) over the amber against
  // rgb(38,119,198) over the greens at the top, so the gradient underneath
  // still shows through as a shift in the blue. The pale right edge keeps the
  // fill from running into the next-achievable dot on that boundary.
  gapFill: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    transformOrigin: 'left center',
    backgroundColor: 'rgba(46, 111, 217, 0.82)',
    borderRight: '1px solid rgba(255, 255, 255, 0.9)',
    animation: '$gapGrow 1.1s ease-out 0.25s both',
  },
  numberLabel: {
    position: 'absolute',
    top: (props) => `${(parseInt(props.barHeight, 10) || 24) + 11}px`,
    textAlign: 'center',
  },
}));

// Head shape and line style per marker kind, from the score-scale spec: the
// current score is a dark dotted line with an outlined circle, `cleared` is
// that same score once it has reached the goal - green, with a filled centre -
// the next achievable score is a blue solid line with a filled circle, the
// ideal-candidate goal a green dotted line with a diamond, and `merged` is the
// case where two of them coincide, drawn as concentric circles.
//
// `labelBelow` drops a label under the scale numerals instead of above the bar.
// The current score sits within half a point of the next achievable one at the
// low end of the scale, so keeping both above guarantees a collision.
//
// `stackedLabel` gives the value a line of its own above the caption. The
// current score gets it because that number is the one the candidate came here
// to read; a merged marker gets it because its one value answers to two
// captions and cannot sit inline with both.
const MarkerShapes = {
  current: { head: 'ring', stroke: 'dotted', labelBelow: true, stackedLabel: true },
  cleared: {
    head: 'ring', stroke: 'dotted', labelBelow: true, stackedLabel: true, filledCore: true
  },
  next: { head: 'filled', stroke: 'solid' },
  ideal: { head: 'diamond', stroke: 'dotted' },
  merged: { head: 'concentric', stroke: 'dotted', stackedLabel: true },
};

// Geometry of a marker column, from the bottom up: the stem ends STEM_BELOW_BAR
// past the bottom edge of the bar so every line runs on through the scale
// numerals, and the head is centred in its slot at the top.
//
// A marker whose label goes below the bar lengthens its stem by
// BELOW_LABEL_STEM_EXTRA so the line reaches that label, and drops its whole
// column by the same amount - which is what keeps its head level with the rest
// instead of riding up with the longer stem.
// MARKER_STEM_HEIGHT sets how far the heads stand off the top of the bar: at 60
// the centre of every head lands 54px above the bar's bottom edge, which on the
// 32px case-master bar is 22px of clear air above it.
const HEAD_SLOT_HEIGHT = 28;
const MARKER_STEM_HEIGHT = 60;
const STEM_BELOW_BAR = 20;
const BELOW_LABEL_STEM_EXTRA = 12;

// Label line box heights, used only to reserve room around the bar.
const VALUE_LINE_HEIGHT = 24;
const CAPTION_LINE_HEIGHT = 18;
const LABEL_GAP = 6;
const LABEL_MARGIN = 8;

// Vertical step between two above-bar labels that would otherwise collide, and
// how close their markers have to be - as a percentage of the bar width - for
// that to happen. Value and caption on one line runs to about 175px, so on an
// 880px bar two of them need a fifth of the bar between their centres to clear
// each other. Any closer and the right-hand label moves up a row.
//
// The step has to stay clear of the label height, or the raised label lands on
// top of the line below it rather than above it. Both above-bar labels are a
// single 14px line, so 32 leaves room to spare; a stacked label runs to 42px
// and would not fit, which is the other reason only the below-bar one stacks.
// The space reserved for them still counts a full VALUE_LINE_HEIGHT, so the bar
// holds its position on the page whatever size the value line is set in.
const LABEL_ROW_HEIGHT = 32;
const LABEL_COLLISION_GAP = 20;

// A label centred on a score at either end of the bar would hang off it, so
// near the ends it is anchored by the edge that faces inwards. The stacked
// labels are far narrower than the single-line ones they replaced, so this only
// needs to bite in the last twentieth of the scale.
const labelAnchor = (position) => {
  if (position <= 5) return { left: 0, transform: 'none' };
  if (position >= 95) return { right: 0, transform: 'none' };

  return { left: '50%', transform: 'translateX(-50%)' };
};

const shapeOf = (variant) => MarkerShapes[variant] || MarkerShapes.current;

// Height a label block takes up, so the bar can reserve room for it. An inline
// label is one line however many words it holds; a stacked one adds a row per
// caption under its value.
const labelHeight = (marker) => {
  if (!marker.valueLabel) return 0;

  return shapeOf(marker.variant).stackedLabel
    ? VALUE_LINE_HEIGHT + ((marker.captions || []).length * CAPTION_LINE_HEIGHT)
    : VALUE_LINE_HEIGHT;
};

const ScoreBar = ({
  scale = 5, maxScale = 5, width, barHeight, markers = [], gap = null
}) => {
  const classes = useStyles({ maxScale, width, barHeight, rounded: markers.length > 0 });
  const barElementRef = React.useRef(null);

  // Create divider positions based on maxScale
  const Divider_Positions = React.useMemo(() => {
    const positions = [];
    for (let i = 0; i < maxScale; i++) {
      positions.push((i / (maxScale - 1)) * 100);
    }
    return positions;
  }, [maxScale]);

  const scaledWidth = React.useMemo(() => {
    const index = Math.floor(scale) - 1;
    const nextIndex = Math.ceil(scale) - 1;

    if (index === nextIndex) {
      return Divider_Positions[index];
    }

    const fraction = scale - Math.floor(scale);
    return (
      Divider_Positions[index] +
      (Divider_Positions[nextIndex] - Divider_Positions[index]) * fraction
    );
  }, [scale, Divider_Positions]);

  // Where a score sits along the bar, as a percentage of its width.
  const positionOf = React.useCallback((value) => {
    const clamped = Math.min(Math.max(value, 1), maxScale);
    return ((clamped - 1) / (maxScale - 1)) * 100;
  }, [maxScale]);

  // Which row each above-bar label belongs on, keyed by marker index. Labels
  // share the bottom row wherever there is room - the next achievable score and
  // the goal are usually points apart - and only the crowded ones step up, so
  // nothing gets staggered that did not need to be.
  const labelRows = React.useMemo(() => {
    const rows = {};

    const above = markers
      .map((marker, index) => ({ index, position: positionOf(marker.value) }))
      .filter(({ index }) => (
        markers[index].valueLabel && !shapeOf(markers[index].variant).labelBelow
      ))
      .sort((a, b) => a.position - b.position);

    above.forEach(({ index, position }, order) => {
      const previous = above[order - 1];

      rows[index] = previous && (position - previous.position) < LABEL_COLLISION_GAP
        ? rows[previous.index] + 1
        : 0;
    });

    return rows;
  }, [markers, positionOf]);

  // A label under the numerals needs the container to reserve room for it,
  // otherwise it overflows into whatever follows the bar.
  const hasLabelBelow = markers.some((marker) => (
    marker.valueLabel && shapeOf(marker.variant).labelBelow
  ));

  // Room to leave around the bar, worked out from the geometry above rather
  // than guessed: a merged marker carries two captions on its label and a
  // stepped-up one sits a whole row higher.
  const [spaceAbove, spaceBelow] = React.useMemo(() => {
    if (!markers.length) return [28, 28];

    const barHeightPx = parseInt(barHeight, 10) || 24;
    const headTopAboveBar = MARKER_STEM_HEIGHT + HEAD_SLOT_HEIGHT - STEM_BELOW_BAR - barHeightPx;

    const tallestAbove = markers.reduce((tallest, marker, index) => (
      shapeOf(marker.variant).labelBelow
        ? tallest
        : Math.max(tallest, ((labelRows[index] || 0) * LABEL_ROW_HEIGHT) + labelHeight(marker))
    ), 0);

    const tallestBelow = markers.reduce((tallest, marker) => (
      shapeOf(marker.variant).labelBelow ? Math.max(tallest, labelHeight(marker)) : tallest
    ), 0);

    return [
      headTopAboveBar + LABEL_GAP + tallestAbove + LABEL_MARGIN,
      hasLabelBelow
        ? STEM_BELOW_BAR + BELOW_LABEL_STEM_EXTRA + tallestBelow + LABEL_MARGIN
        : 28,
    ];
  }, [markers, labelRows, barHeight, hasLabelBelow]);

  React.useEffect(() => {
    const barElement = barElementRef.current;

    if (barElement) {
      barElement.classList.remove(classes.innerBar);

      void barElement.offsetWidth;

      barElement.classList.add(classes.innerBar);
      barElement.style.width = `${scaledWidth}%`;
    }
  }, [scale]);

  return (
    <Box
      width="100%"
      display="flex"
      justifyContent="center"
      style={{ marginTop: `${spaceAbove}px`, marginBottom: `${spaceBelow}px` }}
    >
      <Box className={classes.gradientBar}>
        {Divider_Positions.map((position, index) => (
          <Box key={index} position="relative" className={classes.divider}  style={{ left: `${position}%` }}>
            <Typography variant='body02-semiBold' className={classes.numberLabel}>{index + 1}</Typography>
          </Box>
        ))}
        {
          // The gap the candidate can close on a reattempt.
          gap && (
            <Box
              className={classes.gap}
              style={{
                left: `${positionOf(gap.from)}%`,
                width: `${positionOf(gap.to) - positionOf(gap.from)}%`
              }}
            >
              <Box className={classes.gapFill} />
            </Box>
          )
        }

        {markers.map((marker, index) => {
          const {
            value, valueLabel, captions = [], color, coreColor, variant, pulse, hideHead
          } = marker;

          // Every marker hangs above the bar with its line running through it.
          // The kind decides the head shape and whether the line is dotted.
          const { head, stroke, labelBelow, stackedLabel, filledCore } = shapeOf(variant);

          const stemExtra = labelBelow ? BELOW_LABEL_STEM_EXTRA : 0;
          const row = labelRows[index] || 0;

          const label = valueLabel && (
            <Box
              className={labelBelow ? classes.markerLabelBelow : classes.markerLabelAbove}
              style={{
                ...labelAnchor(positionOf(value)),
                ...(labelBelow ? {} : { marginBottom: `${LABEL_GAP + (row * LABEL_ROW_HEIGHT)}px` })
              }}
            >
              {stackedLabel ? (
                <>
                  <Typography variant='h6-bold' className={classes.labelValue}>
                    {valueLabel}
                  </Typography>

                  {/* A merged marker speaks for two benchmarks at once, so its
                      captions stack under the one value they share. */}
                  {captions.map(({ text, color: captionColor }) => (
                    <Typography
                      key={text}
                      variant='body02-medium'
                      className={classes.labelCaption}
                      style={{ color: captionColor }}
                    >
                      {text}
                    </Typography>
                  ))}
                </>
              ) : (
                <Box className={classes.labelInline}>
                  <Typography variant='body02-bold' style={{ color: captions[0]?.color }}>
                    {valueLabel}
                  </Typography>
                  <Typography variant='body02-medium' style={{ color: captions[0]?.color }}>
                    {captions[0]?.text}
                  </Typography>
                </Box>
              )}
            </Box>
          );

          return (
            <Box
              key={`${variant}-${value}`}
              className={classes.marker}
              style={{
                left: `${positionOf(value)}%`,
                bottom: `-${STEM_BELOW_BAR + stemExtra}px`
              }}
            >
              {!labelBelow && label}

              {/* Two markers can land on the same value; one of them draws no
                  head so the shapes do not stack, keeping its line and label. */}
              <Box className={classes.headSlot}>
                <Box className={pulse ? classes.markerPulse : undefined}>
                  {!hideHead && head === 'ring' && (
                    <Box className={classes.markerRing} style={{ borderColor: color }}>
                      {filledCore && (
                        <Box
                          className={classes.markerRingCore}
                          style={{ backgroundColor: color }}
                        />
                      )}
                    </Box>
                  )}
                  {!hideHead && head === 'filled' && (
                    <Box className={classes.markerFilled} style={{ backgroundColor: color }} />
                  )}
                  {!hideHead && head === 'diamond' && (
                    <Box className={classes.markerDiamond} style={{ backgroundColor: color }} />
                  )}
                  {!hideHead && head === 'concentric' && (
                    <Box className={classes.markerConcentric} style={{ borderColor: color }}>
                      <Box
                        className={classes.markerConcentricCore}
                        style={{ backgroundColor: coreColor }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Grown upward by half a head slot and pulled back by the same
                  amount, so the line reaches the head's centre without moving
                  the head or changing the column's height. */}
              <Box
                className={classes.markerStem}
                style={{
                  height: `${MARKER_STEM_HEIGHT + stemExtra + (HEAD_SLOT_HEIGHT / 2)}px`,
                  marginTop: `-${HEAD_SLOT_HEIGHT / 2}px`,
                  borderLeftStyle: stroke,
                  borderLeftColor: color
                }}
              />

              {labelBelow && label}
            </Box>
          );
        })}

        {
          // Without explicit markers the bar keeps its original single pointer.
          !markers.length && (
            <Box ref={barElementRef}>
              <Box className={classes.scaleContainer}>
                <Box className={classes.triangle}></Box>
                <Box className={classes.line}></Box>
              </Box>
            </Box>
          )
        }
      </Box>
    </Box>
  );
}

export default ScoreBar;
